import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../../../auth/AuthContext";
import DashboardService from "../../../../services/DashboardService";
import AlertService from "../../../../services/AlertService";

const UpdateLabHoursCounts = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const submitControllerRef = useRef(null);

   useEffect(() => {
    const labhoursAccess = authContext.getPermissions("updatelabhours");
    if (labhoursAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate("/");
    }
    setPermissions({
      showAdd: labhoursAccess.some(f => f.access.includes(2)),
      showEdit: labhoursAccess.some(f => f.access.includes(3)),
      showDelete: labhoursAccess.some(f => f.access.includes(4)),
    });
  }, [authContext, navigate]);

  const [formData, setFormData] = useState({
    totalIncidents: "",
    totalLearners: "",
    totalLabHours: "",
    decommissioned: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // GET 
useEffect(() => {
    const controller = new AbortController();

    const fetchLabHours = async () => {
      setLoading(true);
      try {
        const response = await DashboardService.GetLabHours(authContext, controller.signal );

        if (controller.signal.aborted) return; 

        if (response?.isSuccess && response?.data) {
          const map = {};
          response.data.forEach((item) => {
            map[item.configType] = item.configValue;
          });
          setFormData({
            totalIncidents: map.TotalIncidents || "",
            totalLearners: map.TotalLearners || "",
            totalLabHours: map.TotalLabHours || "",
            decommissioned: map.DECOMMISSIONED || "",
          });
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchLabHours();

    return () => controller.abort(); 
  }, [authContext]);



const handleSubmit = async (e) => {
  e.preventDefault();

  if (submitControllerRef.current) {
    submitControllerRef.current.abort(); 
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setSubmitting(true);

  const payload = [
    { key: "TotalIncidents", value: formData.totalIncidents },
    { key: "TotalLearners", value: formData.totalLearners },
    { key: "TotalLabHours", value: formData.totalLabHours },
    { key: "DECOMMISSIONED", value: formData.decommissioned }
  ];

  try {
    const res = await DashboardService.SaveLabHours(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return; 

    if (res?.isSuccess) {
      AlertService.success("Lab hours updated successfully!");
      navigate('/dashboard');
    } else {
      AlertService.error(res?.errorMessage || "Something went wrong");
    }

  } catch (err) {
    if (err.name === "AbortError") return; 
    console.error(err);
    AlertService.error("Failed to update lab hours");
  } finally {
    if (!controller.signal.aborted) setSubmitting(false);
  }
};

useEffect(() => {
    return () => {
      submitControllerRef.current?.abort(); 
    };
  }, []);

  return (
    <div className="container-fluid pt-4 px-3">
      <form onSubmit={handleSubmit}>
        {/* Total Incidents */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-2">
            <label className="form-label mb-0">Total Incidents</label>
          </div>
          <div className="col-md-10">
            <input
              type="number"
              className="form-control custom-input custom-input-color"
              value={formData.totalIncidents}
              disabled={loading}
              onChange={(e) =>
                handleChange("totalIncidents", e.target.value)
              }
            />
          </div>
        </div>

        {/* Total Learners */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-2">
            <label className="form-label mb-0">Total Learners</label>
          </div>
          <div className="col-md-10">
            <input
              type="number"
              className="form-control custom-input custom-input-color"
              value={formData.totalLearners}
              disabled={loading}
              onChange={(e) =>
                handleChange("totalLearners", e.target.value)
              }
            />
          </div>
        </div>

        {/* Total Lab Hours */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-2">
            <label className="form-label mb-0">Total Lab Hours</label>
          </div>
          <div className="col-md-10">
            <input
              type="number"
              className="form-control custom-input custom-input-color"
              value={formData.totalLabHours}
              disabled={loading}
              onChange={(e) =>
                handleChange("totalLabHours", e.target.value)
              }
            />
          </div>
        </div>

        {/* Decommission Incidents */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-2">
            <label className="form-label mb-0">
              Total Documents
            </label>
          </div>
          <div className="col-md-10">
            <input
              type="number"
              className="form-control custom-input custom-input-color"
              value={formData.decommissioned}
              disabled={loading}
              onChange={(e) =>
                handleChange("decommissioned", e.target.value)
              }
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="row mt-4">
          <div className="col-md-2"></div>

          <div className="col-md-10 d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-outline px-5 py-2"
              disabled={loading}
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </button>

{(permissions.showAdd || permissions.showEdit) && (
  <button
    type="submit"
    className="btn btn-primary text-white px-5 py-2"
    disabled={loading || submitting}
  >
    {submitting ? "Submitting..." : "Submit"}
  </button>
)}
          </div>
        </div>
      </form>
    </div>
  );
};

export default UpdateLabHoursCounts;